import PageHero from '@/components/PageHero'
import SEOHead from '@/components/SEOHead'
import {BarChart3, ShieldCheck, Target, Users} from 'lucide-react'

const values = [
  {
    icon: Target,
    title: 'Precision First',
    description: 'Every formula on AdsROAS Calculator is built on standard, transparent marketing math — no hidden assumptions, no black boxes.',
  },
  {
    icon: ShieldCheck,
    title: 'Independent & Free',
    description: 'We are not affiliated with Google, Meta, or Amazon. AdsROAS Calculator is an independent utility, free to use with no signup required.',
  },
  {
    icon: Users,
    title: 'Built for Practitioners',
    description: 'Designed with media buyers, agencies, and e-commerce founders in mind — the people who make ad-spend decisions every day.',
  },
  {
    icon: BarChart3,
    title: 'Data-Driven Decisions',
    description: 'Our goal is simple: help you turn raw ad spend and revenue numbers into clear, actionable profitability insights instantly.',
  },
]

function About() {
  return (
    <div>
      <SEOHead
        title="About Us"
        description="Learn about AdsROAS Calculator, an independent ad-tech utility built for media buyers, agencies, and e-commerce entrepreneurs to measure advertising profitability."
      />

      <PageHero
        eyebrow="Independent ad-tech utility"
        title="About AdsROAS Calculator"
        description="An independent tool designed for media buyers, agencies, and e-commerce entrepreneurs who need fast, accurate advertising profitability calculations."
      />

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="max-w-3xl">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400 mb-3">Our mission</p>
          <h2 className="text-2xl sm:text-[1.7rem] font-extrabold text-slate-900 tracking-tight">Clear math for paid media decisions</h2>
          <p className="mt-4 text-[15px] text-slate-600 leading-relaxed">
            AdsROAS Calculator was built on a simple premise: advertisers should not need a spreadsheet degree
            to understand whether their campaigns are profitable. Return on Ad Spend (ROAS), Return on Investment (ROI),
            break-even points, and platform-specific conversion math are core to running healthy paid media —
            yet the calculations are often scattered across blog posts, agency decks, or manually rebuilt in
            Google Sheets every time. We centralize these calculations into fast, accurate, and free tools.
          </p>
          <p className="mt-4 text-[15px] text-slate-600 leading-relaxed">
            Whether you are a solo e-commerce founder validating a new Google Ads campaign, an agency analyst
            reporting on Facebook Ads performance, or a seller reconciling Amazon ACoS against a standard ROAS
            benchmark, AdsROAS Calculator gives you an instant, reliable answer.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {values.map((value) => {
            const Icon = value.icon
            return (
              <div key={value.title} className="bg-white border border-slate-200 rounded-2xl p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
                <div className="w-10 h-10 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-emerald-600" />
                </div>
                <h3 className="font-extrabold text-slate-900 tracking-tight">{value.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{value.description}</p>
              </div>
            )
          })}
        </div>

        <div className="mt-10 bg-emerald-50 border border-emerald-100 rounded-2xl p-6 sm:p-7">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-700 mb-2">Independence</p>
          <h3 className="font-extrabold text-slate-900 tracking-tight">A note on platform affiliations</h3>
          <p className="mt-2 text-sm text-slate-700 leading-relaxed">
            AdsROAS Calculator is not affiliated with, endorsed by, or sponsored by Google, Meta, Amazon, or any
            other advertising platform. All calculations are based on publicly documented marketing formulas and
            are provided for informational and planning purposes only.
          </p>
        </div>
      </section>
    </div>
  )
}

export default About
